import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Missing reset token. Request a new link.");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    if (password.length < 10) {
      toast.error("Password must be at least 10 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success("Password reset! Redirecting to login...");
      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Failed to reset password. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-md w-full bg-card border-primary/30" data-testid="reset-password-card">
        <CardContent className="p-8 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-sm border-2 border-primary/40 flex items-center justify-center">
              <KeyRound className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-black text-white uppercase">Reset Password</h1>
              <p className="text-xs text-muted-foreground">Pick a new password to access your account.</p>
            </div>
          </div>

          {!token && (
            <div className="bg-destructive/10 border border-destructive/30 p-4 rounded-sm flex items-start gap-3" data-testid="reset-no-token-banner">
              <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-bold text-destructive mb-1">Missing reset token</p>
                <p className="text-muted-foreground">This link is invalid. Request a new password reset from the login page.</p>
              </div>
            </div>
          )}

          {success ? (
            <div className="py-6 text-center space-y-3" data-testid="reset-password-success">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
              <p className="text-sm">Password reset successfully!</p>
              <p className="text-xs text-muted-foreground">Redirecting to login...</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="new-password" className="text-xs uppercase font-bold tracking-wider">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Min 10 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={!token || submitting}
                  className="mt-1"
                  data-testid="new-password-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Must be 10+ chars, include uppercase, lowercase, number, and symbol.</p>
              </div>
              <div>
                <Label htmlFor="confirm-password" className="text-xs uppercase font-bold tracking-wider">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Re-type your new password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  disabled={!token || submitting}
                  className="mt-1"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting || !token || !password || !confirm}
                className="w-full bg-primary hover:bg-primary/90 font-heading text-base h-11"
                data-testid="reset-password-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> RESETTING...</>
                ) : (
                  "RESET PASSWORD"
                )}
              </Button>
            </form>
          )}

          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="w-full"
            data-testid="reset-back-to-login-btn"
          >
            Back to Login
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
